import React from "react";
import { currentProjects } from "../data/portfolioData";

const Research: React.FC = () => {
  return (
    <section className="section active">
      {/* Hero section with image */}
      <div className="hero">
        <h1>AI Empathy Research</h1>
        <img
          src="/images/AI-Empathy-Research-Alex-Mari-Founder.jpg"
          alt="Dr. Alex Mari - AI Empathy Research"
          className="hero-image"
        />
      </div>

      {/* Main content with consistent spacing */}
      <div className="bio">
        <h2>About the AI Empathy Research Initiative</h2>
        <div className="glass card">
          <p>
            The AI Empathy Research Initiative brings together senior
            researchers to explore how{" "}
            <strong>AI-powered voice assistants</strong> can transform human
            decision-making and emotional well-being. The Initiative
            investigates how empathic{" "}
            <strong>AI can improve interactions</strong> in healthcare, retail,
            and education, while also addressing its potential risks and ethical
            concerns.
          </p>

          <p>
            Established in 2024 with support from the{" "}
            <a
              href="https://www.uzhfoundation.ch/en/"
              target="_blank"
              rel="noopener noreferrer"
              className="publication-link"
            >
              UZH Foundation
            </a>{" "}
            (University of Zurich) and the{" "}
            <a
              href="https://mtec.ethz.ch/research/support/mtec-foundation.html"
              target="_blank"
              rel="noopener noreferrer"
              className="publication-link"
            >
              MTEC Foundation
            </a>{" "}
            (ETH Zurich), this academic collaboration aims to generate
            actionable insights for both managers and policymakers. For
            businesses, empathic AI offers opportunities to design more
            <strong> human-centric technologies</strong>; for policymakers, it
            raises critical questions about AI's influence on personal choices
            and the need for appropriate regulation.
          </p>

          <p>
            By examining both the ethics and the effectiveness of empathy
            simulation, the AI Empathy Research Initiative envisions AI voice
            assistants that embody key dimensions of empathy—perspective-taking,
            emotional resonance, and prosocial motivation—to be perceived as
            <strong> authentic, trustworthy, and supportive</strong>,
            particularly when emotional connections have already been
            established.
          </p>

          <p>
            The integration of{" "}
            <a
              href="https://www.hume.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="publication-link"
            >
              Hume AI
            </a>{" "}
            technologies has further enriched the group's experimental design by
            enabling the development of AI assistants capable of{" "}
            <strong>detecting and responding to user emotions</strong> in real
            time. This capability allows for a more nuanced understanding of AI
            empathy's role in human-AI interactions, decision-making, and
            emotional well-being, advancing both scientific knowledge and
            real-world applications.
          </p>

          <p style={{ marginBottom: "0" }}>
            Current scholars affiliated with the{" "}
            <strong>AI Empathy Research Initiative:</strong>
          </p>

          <div style={{ lineHeight: "1.4", marginBottom: "1rem" }}>
            <div
              className="publication-item"
              style={{ display: "flex", alignItems: "flex-start" }}
            >
              <span
                style={{ minWidth: "25px", fontSize: "16px", flexShrink: 0 }}
              >
                –
              </span>
              <span style={{ paddingLeft: "20px", flex: 1 }}>
                <a
                  href="https://scholar.google.com/citations?user=Wa6NNEEAAAAJ&hl=en"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="publication-link"
                  style={{ color: "#0066cc" }}
                >
                  Dr. Alex Mari
                </a>
                , Assistant Professor of Marketing, American University of
                Bahrain
              </span>
            </div>
            <div
              className="publication-item"
              style={{ display: "flex", alignItems: "flex-start" }}
            >
              <span
                style={{ minWidth: "25px", fontSize: "16px", flexShrink: 0 }}
              >
                –
              </span>
              <span style={{ paddingLeft: "20px", flex: 1 }}>
                <a
                  href="https://scholar.google.com/citations?user=XvjSAZUAAAAJ&hl=en"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="publication-link"
                  style={{ color: "#0066cc" }}
                >
                  Dr. Ertugrul Uysal
                </a>
                , Senior Researcher, ETH Zurich
              </span>
            </div>
            <div
              className="publication-item"
              style={{ display: "flex", alignItems: "flex-start" }}
            >
              <span
                style={{ minWidth: "25px", fontSize: "16px", flexShrink: 0 }}
              >
                –
              </span>
              <span style={{ paddingLeft: "20px", flex: 1 }}>
                <a
                  href="https://scholar.google.com/citations?user=-v9t8YMAAAAJ&hl=en"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="publication-link"
                  style={{ color: "#0066cc" }}
                >
                  Dr. Fotis Efthymiou
                </a>
                , Research Scientist, AI Empathy
              </span>
            </div>
            <div
              className="publication-item"
              style={{ display: "flex", alignItems: "flex-start" }}
            >
              <span
                style={{ minWidth: "25px", fontSize: "16px", flexShrink: 0 }}
              >
                –
              </span>
              <span style={{ paddingLeft: "20px", flex: 1 }}>
                <a
                  href="https://scholar.google.com/citations?user=89byC1UAAAAJ"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="publication-link"
                  style={{ color: "#0066cc" }}
                >
                  Dr. Jeff Brooks
                </a>
                , Research Scientist, Hume AI
              </span>
            </div>
            <div
              className="publication-item"
              style={{ display: "flex", alignItems: "flex-start" }}
            >
              <span
                style={{ minWidth: "25px", fontSize: "16px", flexShrink: 0 }}
              >
                –
              </span>
              <span style={{ paddingLeft: "20px", flex: 1 }}>
                <a
                  href="https://scholar.google.com/citations?user=qKDGtpYAAAAJ&hl=en"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="publication-link"
                  style={{ color: "#0066cc" }}
                >
                  Dr. Amani Alabed
                </a>
                , Assistant Professor of Digital Marketing, University of Doha
                for Science and Technology
              </span>
            </div>
            <div
              className="publication-item"
              style={{ display: "flex", alignItems: "flex-start" }}
            >
              <span
                style={{ minWidth: "25px", fontSize: "16px", flexShrink: 0 }}
              >
                –
              </span>
              <span style={{ paddingLeft: "20px", flex: 1 }}>
                <a
                  href="https://www.linkedin.com/in/danylo-provodov/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="publication-link"
                  style={{ color: "#0066cc" }}
                >
                  Danylo Provodov
                </a>
                , Computer Science, Research Assistant
              </span>
            </div>
          </div>
        </div>

        <div className="research-section">
          <h2>Main Projects</h2>
          {currentProjects.map((project) => (
            <div
              key={project.id}
              className="research-project glass card"
              style={{ marginBottom: "2rem" }}
            >
              <div className="project-title">
                <strong>{project.id}. Title: </strong>
                {project.title}
              </div>
              <div className="project-team">
                <strong>Team:</strong>{" "}
                <span
                  dangerouslySetInnerHTML={{
                    __html: project.team.replace(
                      /Alex Mari \(AUBH\)/g,
                      "<strong>Alex Mari (AUBH)</strong>"
                    ),
                  }}
                />
              </div>
              <div className="project-abstract">
                <strong>Abstract:</strong> {project.abstract}
                {project.fullAbstract && (
                  <span>
                    {" " +
                      project.fullAbstract.substring(project.abstract.length)}
                    {project.link && (
                      <>
                        <br />
                        <br />
                        <a
                          href={project.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="navigation-arrow"
                        >
                          {">> Preliminary results (Hume AI)"}
                        </a>
                      </>
                    )}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Partner Logos Section - Infinite Carousel */}
        <div className="research-section">
          <div className="partner-logos-wrapper">
            <div className="partner-logos-carousel">
              <div className="partner-logos-track">
                {/* First set of logos */}
                <div className="partner-logos-group">
                  <a
                    href="https://hume.ai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="partner-logo-link"
                  >
                    <img
                      src="/images/hume.png"
                      alt="Hume AI"
                      className="partner-logo"
                    />
                  </a>
                  <a
                    href="https://ethz.ch/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="partner-logo-link"
                  >
                    <img
                      src="/images/eth.png"
                      alt="ETH Zurich"
                      className="partner-logo"
                    />
                  </a>
                  <a
                    href="https://www.caryally.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="partner-logo-link"
                  >
                    <img
                      src="/images/CaryAlly_horizontal.svg"
                      alt="Cary Ally"
                      className="partner-logo"
                    />
                  </a>
                  <a
                    href="https://hearinghome.ch/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="partner-logo-link"
                  >
                    <img
                      src="/images/hearing_home.png"
                      alt="Hearing Home"
                      className="partner-logo"
                    />
                  </a>
                </div>

                {/* Duplicate set for seamless loop */}
                <div className="partner-logos-group">
                  <a
                    href="https://hume.ai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="partner-logo-link"
                  >
                    <img
                      src="/images/hume.png"
                      alt="Hume AI"
                      className="partner-logo"
                    />
                  </a>
                  <a
                    href="https://ethz.ch/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="partner-logo-link"
                  >
                    <img
                      src="/images/eth.png"
                      alt="ETH Zurich"
                      className="partner-logo"
                    />
                  </a>
                  <a
                    href="https://www.caryally.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="partner-logo-link"
                  >
                    <img
                      src="/images/CaryAlly_horizontal.svg"
                      alt="Cary Ally"
                      className="partner-logo"
                    />
                  </a>
                  <a
                    href="https://hearinghome.ch/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="partner-logo-link"
                  >
                    <img
                      src="/images/hearing_home.png"
                      alt="Hearing Home"
                      className="partner-logo"
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Research;
